import React from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { collectCarrierScoringBreakdown } from "./scoringExplanationCache";

export const formatScoreValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : value.toFixed(4);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const kpiFactorLabel = (row, index) =>
  row?.name ||
  row?.factor ||
  row?.kpi ||
  row?.kpi_name ||
  row?.kpiName ||
  row?.type ||
  row?.label ||
  `KPI ${index + 1}`;

const rowWeight = (row) =>
  row?.weight ??
  row?.percent ??
  row?.weight_pct ??
  row?.weightPct ??
  row?.kpi_weight ??
  row?.kpiWeight;

const rowScore = (row) =>
  row?.score ??
  row?.value ??
  row?.factor_value ??
  row?.factorValue ??
  row?.normalized_score ??
  row?.normalizedScore;

const rowValue = (row) => rowScore(row) ?? row?.cost ?? row?.price;

const rowContribution = (row) =>
  row?.contribution ??
  row?.weighted_score ??
  row?.weightedScore ??
  row?.kpi_contribution ??
  row?.kpiContribution;

const hasMetric = (getter, rows) =>
  rows.some((row) => {
    const value = getter(row);
    return value != null && value !== "";
  });

const isCostKpiRow = (row) => {
  const key = String(row?.name || row?.kpi || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return key === "cost" || key === "price";
};

export const renderScoreDetailsTable = (rows, { title, valueLabel = "Value" } = {}) => {
  if (!rows?.length) return null;

  const showWeight = hasMetric(rowWeight, rows);
  const showContribution = hasMetric(rowContribution, rows);

  return (
    <div>
      {title ? <p className="mb-2 font-semibold">{title}</p> : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-dense-xs font-semibold uppercase tracking-wide text-gray-700">
            <tr>
              <th className="px-3 py-2">{valueLabel === "Score" ? "KPI" : "Factor"}</th>
              {showWeight ? <th className="px-3 py-2 text-right">Weight</th> : null}
              <th className="px-3 py-2 text-right">{valueLabel}</th>
              {showContribution ? (
                <th className="px-3 py-2 text-right">Contribution</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${kpiFactorLabel(row, index)}-${index}`}
                className="border-t border-gray-100"
              >
                <td className="px-3 py-2">{kpiFactorLabel(row, index)}</td>
                {showWeight ? (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowWeight(row))}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(valueLabel === "Score" ? rowScore(row) : rowValue(row))}
                </td>
                {showContribution ? (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowContribution(row))}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderKpiMetricsTable = (rows) => {
  const displayRows = (rows || []).filter((row) => !isCostKpiRow(row));
  if (!displayRows.length) return null;

  const showKpiFactorFlag = displayRows.some((row) => row.usedInKpiFactor != null);
  const usesPenaltyColumn = displayRows.some((row) => row.penaltyMultiplier != null);

  return (
    <div>
      <p className="mb-1 font-semibold">Carrier KPIs</p>
      {usesPenaltyColumn ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-700">
          Penalty multiplier is derived from each KPI&apos;s performance rate (lower is better).
          KPIs with KPI factor Yes contribute weight × penalty multiplier to the overall KPI factor.
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead className="bg-gray-50 text-dense-xs font-semibold text-gray-700">
            <tr>
              <th className="w-[36%] px-3 py-2 text-left">KPI</th>
              <th className="w-[22%] px-3 py-2 text-right">
                {usesPenaltyColumn ? "Penalty multiplier" : "Value"}
              </th>
              <th className="w-[22%] px-3 py-2 text-right">Performance rate</th>
              {showKpiFactorFlag ? (
                <th className="w-[20%] px-3 py-2 text-center">KPI factor</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, index) => (
              <tr key={`${kpiFactorLabel(row, index)}-${index}`} className="border-t border-gray-100">
                <td className="px-3 py-2 text-left">{kpiFactorLabel(row, index)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(
                    usesPenaltyColumn ? row.penaltyMultiplier : rowScore(row)
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(row.onTimeRate)}
                </td>
                {showKpiFactorFlag ? (
                  <td className="px-3 py-2 text-center">
                    {row.usedInKpiFactor ? "Yes" : row.usedInKpiFactor === false ? "No" : "—"}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderCalculationSection = (title, calc) => {
  const { formula, kpiFactor, steps = [] } = calc ?? {};
  if (!formula && !steps.length && kpiFactor == null) return null;

  const showNormalized = steps.some((row) => row.normalized != null && row.normalized !== "");
  const isKpiFactorSection = /kpi factor/i.test(title || "");
  const rawColumnLabel = isKpiFactorSection ? "Penalty multiplier" : "Raw";

  return (
    <div>
      <p className="mb-2 font-semibold">{title}</p>
      {formula ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">{formula}</p>
      ) : null}
      {isKpiFactorSection && kpiFactor != null ? (
        <p className="mb-2 text-dense-xs text-gray-700">
          KPI factor: {formatScoreValue(kpiFactor)}
        </p>
      ) : null}
      {steps.length ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-dense-xs font-semibold uppercase tracking-wide text-gray-700">
              <tr>
                <th className="px-3 py-2">Metric</th>
                <th className="px-3 py-2 text-right">Weight</th>
                <th className="px-3 py-2 text-right">{rawColumnLabel}</th>
                {showNormalized ? (
                  <th className="px-3 py-2 text-right">Normalized</th>
                ) : null}
                <th className="px-3 py-2 text-right">Contribution</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((row, index) => (
                <tr key={`${kpiFactorLabel(row, index)}-${index}`} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <div>{kpiFactorLabel(row, index)}</div>
                    {row.calculation ? (
                      <div className="text-dense-xs text-gray-600">{row.calculation}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowWeight(row))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowScore(row))}
                  </td>
                  {showNormalized ? (
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatScoreValue(row.normalized)}
                    </td>
                  ) : null}
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowContribution(row))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const renderPriceCalculationSection = (priceCalculation) => {
  if (!priceCalculation?.formula && priceCalculation?.price == null) return null;

  const rows = [
    { name: "Price", value: priceCalculation.cost },
    { name: "KPI factor", value: priceCalculation.kpiFactor },
    { name: "KPI-Cost", value: priceCalculation.price },
  ].filter((row) => row.value != null && row.value !== "");

  return (
    <div>
      <p className="mb-2 font-semibold">RankCost (KPI-Cost) calculation</p>
      {priceCalculation.formula ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">
          {priceCalculation.formula}
        </p>
      ) : null}
      {rows.length ? renderScoreDetailsTable(rows, { title: null, valueLabel: "Value" }) : null}
    </div>
  );
};

export const renderScoreDetailsBreakdown = ({
  summaryRows = [],
  kpiScoreRows = [],
  kpiFactorCalculation = null,
  priceCalculation = null,
  rankingFormula = null,
} = {}) => {
  const showKpiMetricsTable = kpiScoreRows.length > 0;

  if (
    !rankingFormula &&
    !summaryRows.length &&
    !kpiScoreRows.length &&
    !kpiFactorCalculation?.steps?.length &&
    !priceCalculation?.formula &&
    priceCalculation?.price == null
  ) {
    return (
      <p className="text-sm text-gray-700">No KPI factor breakdown is available for this carrier.</p>
    );
  }

  return (
    <div className="space-y-5 text-sm text-[#030229]">
      {rankingFormula ? (
        <div>
          <p className="mb-1 text-dense-xs font-semibold uppercase tracking-wide text-gray-600">
            Carrier rank (RankCost)
          </p>
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">
            {rankingFormula}
          </p>
        </div>
      ) : null}
      {renderScoreDetailsTable(summaryRows, { title: "Score summary", valueLabel: "Value" })}
      {showKpiMetricsTable ? renderKpiMetricsTable(kpiScoreRows) : null}
      {renderPriceCalculationSection(priceCalculation)}
      {renderCalculationSection("KPI factor calculation", kpiFactorCalculation)}
      {!kpiScoreRows.length &&
      !kpiFactorCalculation?.steps?.length &&
      !priceCalculation?.formula ? (
        <p className="text-sm text-gray-700">
          Individual KPI scores are not available yet for this carrier.
        </p>
      ) : null}
    </div>
  );
};

export const findRankedCarrierRow = (rankedCarriers, carrierCode) => {
  const code = (carrierCode || "").toString().trim().toUpperCase();
  if (!code) return null;
  return (rankedCarriers || []).find(
    (row) =>
      (row?.carrier_code || row?.carrierCode || row?.code || "")
        .toString()
        .trim()
        .toUpperCase() === code
  );
};

export const buildCarrierScoreBreakdown = (carrierCode, rankedCarrierRow, scoringPayload = null) =>
  collectCarrierScoringBreakdown(
    scoringPayload ?? rankedCarrierRow,
    carrierCode,
    rankedCarrierRow
  );

export const hasScoreBreakdownData = (breakdown) => {
  if (!breakdown) return false;
  return (
    (breakdown.summaryRows?.length ?? 0) > 0 ||
    (breakdown.kpiScoreRows?.length ?? 0) > 0 ||
    (breakdown.kpiFactorCalculation?.steps?.length ?? 0) > 0 ||
    Boolean(breakdown.priceCalculation?.formula) ||
    breakdown.priceCalculation?.price != null
  );
};

export const renderScoringExplanationBody = (fullPayload, carrierCode, rankedCarrierRow) => {
  if (!fullPayload && !rankedCarrierRow) {
    return (
      <p className="text-sm text-gray-700">No scoring explanation is available for this carrier.</p>
    );
  }

  return renderScoreDetailsBreakdown(
    buildCarrierScoreBreakdown(carrierCode, rankedCarrierRow, fullPayload)
  );
};

export function CarrierScoreDetailsModal({
  carrier,
  onClose,
  children,
  zIndexClass = "z-[1100]",
}) {
  if (!carrier) return null;

  const titleName =
    carrier.displayName || carrier.carrierName || carrier.carrierCode || "Carrier";

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/45 px-4`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-details-title"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 id="score-details-title" className="text-lg font-semibold text-[#030229]">
              Score details
            </h3>
            <p className="mt-1 text-sm text-gray-700">
              {titleName}
              {carrier.carrierCode && !String(titleName).includes(carrier.carrierCode)
                ? ` (${carrier.carrierCode})`
                : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close score details"
          >
            <X size={20} />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {children ??
            (carrier.__scoreBreakdown
              ? renderScoreDetailsBreakdown(carrier.__scoreBreakdown)
              : null)}
        </div>
      </div>
    </div>,
    document.body
  );
}
